<template>
  <Modal v-model="visible" title="AI 生成话术文档" width="640px" :mask-closable="false">
    <div class="ai-generate-doc-container">
      <div class="section">
        <h3>上传文档</h3>
        <p class="desc">上传文档至 AI 服务，生成话术文档表格（HTML 格式）。</p>
        <div class="upload-area">
          <Upload ref="fileUpload" :before-upload="beforeFileUpload" accept="*/*" :show-upload-list="false">
            <Button type="primary" :loading="uploading">
              <Icon type="md-cloud-upload" />
              选择文件
            </Button>
          </Upload>
          <p class="upload-tip">上传文件后执行生成，将自动下载生成的文档。</p>
        </div>
      </div>

      <div class="section">
        <Button type="success" :loading="running" @click="runAiGenerateDoc" :disabled="!uploadedFileId">
          <Icon type="md-play" />
          执行生成并下载
        </Button>
      </div>
    </div>

    <div slot="footer">
      <Button @click="handleCancel" :disabled="uploading || running">关闭</Button>
    </div>
  </Modal>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'

export default {
  name: 'AiGenerateDocModal',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      uploading: false,
      running: false,
      uploadedFileId: '',
      pendingFile: null
    }
  },
  computed: {
    ...mapState('scene', ['sceneId']),
    ...mapState('user', ['company']),
    visible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    userMarker() {
      return `scene-${this.sceneId}`
    },
    difyToken() {
      // const companyId = (this.company && this.company.id) || this.company?.company_id || null
      // const tokens = {
      //   974: 'Bearer app-rjqL8dAfgRHy8XlZomWN4c1l',
      //   6951: 'Bearer app-WimJ8zDodn5wZwWrOb7qsT83',
      //   108257: 'Bearer app-KqasLv6I4xrxwaAQFIpOPmun',
      //   106764: 'Bearer app-fFvO10Bwuzuz2XaYcfAHpLG6'
      // }
      return 'Bearer app-HNVzoYbYQQT13syqW2VP1KCJ'
    }
  },
  methods: {
    handleCancel() {
      this.visible = false
    },

    beforeFileUpload(file) {
      this.pendingFile = file
      this.uploadToDify(file)
      return false
    },

    async uploadToDify(file) {
      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('user', this.userMarker)

        const resp = await axios.post(
          'https://dify.gkscrm.com/v1/files/upload',
          formData,
          {
            headers: {
              Authorization: this.difyToken
            }
          }
        )
        // Dify returns file meta; accept both raw or wrapped
        const data = resp.data || {}
        const fileId = data.id || (data.data && data.data.id)
        if (!fileId) throw new Error('上传失败：未返回文件ID')
        this.uploadedFileId = fileId
        this.$Message.success('文件上传成功')
      } catch (err) {
        console.error('上传文件失败:', err)
        this.$Message.error('上传失败，请重试')
      } finally {
        this.uploading = false
      }
    },

    async runAiGenerateDoc() {
      if (!this.uploadedFileId) return
      this.running = true
      try {
        const payload = {
          response_mode: 'streaming',
          user: this.userMarker,
          inputs: {
            usermassage: [{
              type: "document",
              upload_file_id: this.uploadedFileId,
              transfer_method: "local_file"
            }]
          }
        }

        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 120000)
        const resp = await fetch('https://dify.gkscrm.com/v1/workflows/run', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: this.difyToken
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        })
        clearTimeout(timeoutId)
        if (!resp.ok || !resp.body) throw new Error('网络错误或无流式返回')

        const finalData = await this.readSSE(resp.body)

        const htmlContent = this.parseDocResult(finalData)
        if (!htmlContent) throw new Error('AI 返回结果为空或格式不正确')

        // 解析表格并下载为 CSV
        this.downloadCsvFromHtml(htmlContent)
        this.$Message.success('文档生成并下载成功')
        this.visible = false
      } catch (err) {
        console.error('执行AI生成文档失败:', err)
        this.$Message.error('AI 生成文档失败，请检查文件与网络')
      } finally {
        this.running = false
      }
    },

    async readSSE(readableStream) {
      const reader = readableStream.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''
      let lastData = null

      let isDone = false
      while (!isDone) {
        const chunk = await reader.read()
        isDone = chunk.done === true
        if (isDone) break
        buffer += decoder.decode(chunk.value, { stream: true })

        const events = buffer.split('\n\n')
        buffer = events.pop() || ''
        events.forEach(evt => {
          const lines = evt.split('\n')
          lines.forEach(line => {
            const s = line.trim()
            if (s.startsWith('data:')) {
              const jsonStr = s.replace(/^data:\s*/, '')
              if (jsonStr === '[DONE]') return
              try {
                const obj = JSON.parse(jsonStr)
                lastData = obj
              } catch (e) {
                // ignore partial frames
              }
            }
          })
        })
      }

      return lastData || {}
    },

    parseDocResult(data) {
      try {
        if (!data) return null

        // 尝试获取 text_1 字段
        if (data.text_1) {
          return data.text_1
        }

        // 尝试从 outputs 中获取
        const outputs = data.outputs || (data.data && data.data.outputs)
        if (outputs) {
          if (outputs.text_1) {
            return outputs.text_1
          }
          // 如果 outputs 本身就是包含 text_1 的对象
          if (typeof outputs === 'object' && outputs.text_1) {
            return outputs.text_1
          }
          // 尝试查找任何包含 text_1 的字段
          for (const key in outputs) {
            if (key.includes('text') && typeof outputs[key] === 'string') {
              return outputs[key]
            }
          }
        }

        // 如果 data 是字符串，尝试解析
        if (typeof data === 'string') {
          const parsed = JSON.parse(data)
          return parsed.text_1 || null
        }

        // 如果 data.data 是字符串
        if (data.data && typeof data.data === 'string') {
          const parsed = JSON.parse(data.data)
          return parsed.text_1 || null
        }
      } catch (e) {
        console.warn('解析AI文档结果异常', e)
      }
      return null
    },

    downloadCsvFromHtml(htmlContent) {
      // 提取 table 片段
      const container = document.createElement('div')
      container.innerHTML = htmlContent
      const table = container.querySelector('table')
      if (!table) throw new Error('未找到表格内容')

      const csv = this.htmlTableToCsv(table)
      this.downloadCsv(csv)
    },

    htmlTableToCsv(tableEl) {
      const rows = Array.from(tableEl.querySelectorAll('tr'))
      const toCsvCell = (text) => {
        const t = (text || '').replace(/\r?\n/g, '\n').replace(/\t/g, ' ')
        const needsQuote = /[",\n]/.test(t)
        const escaped = t.replace(/"/g, '""')
        return needsQuote ? `"${escaped}"` : escaped
      }
      const getCellText = (cell) => {
        // 使用 textContent 获取可读文本，保留换行
        return (cell.textContent || '').trim()
      }
      const lines = rows.map(tr => {
        const cells = Array.from(tr.querySelectorAll('th,td'))
        return cells.map(td => toCsvCell(getCellText(td))).join(',')
      })
      return lines.join('\n')
    },

    downloadCsv(csvString) {
      const blob = new Blob(["\ufeff" + csvString], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const today = new Date().toISOString().slice(0, 10)
      const fileName = this.pendingFile.name.split('.').slice(0, -1).join('.')
      link.download = `AI话术文档_${fileName}_${today}.csv`
      link.click()
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-generate-doc-container {
  .section {
    margin-bottom: 16px;
    padding: 16px;
    border: 1px solid #e8eaec;
    border-radius: 6px;
    background-color: #fafafa;

    h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 600;
      color: #17233d;
    }

    .desc {
      margin: 0 0 12px 0;
      color: #808695;
      font-size: 14px;
      line-height: 1.5;
    }

    .upload-area {
      .upload-tip {
        margin: 8px 0 0 0;
        color: #c5c8ce;
        font-size: 12px;
      }
    }
  }
}
</style>
